<template>
  <div class="container">
    <BasicTitle :title="$route.name" />
    <BasicSubtitle subtitle="Los campos señalados con (*) son obligatorios." />
    <div class="container-form">
      <BasicLabel label="Torre *" />
      <BorderSelect
        v-model="apartment.data.towerId"
        label="Torre *"
        :options="apartment.towers"
      />
      <FormError
        :show="apartment.rules.towerId"
        message="Seleccione una opción"
      />
      <BasicLabel label="Número *" />
      <BorderInput
        v-model="apartment.data.number"
        label="Número *"
        @keyup.enter="onSave"
      />
      <FormError
        :show="apartment.rules.number"
        message="Ingrese un número de apartamento"
      />
      <BasicLabel label="Coeficiente de prorrateo *" />
      <BorderInput
        v-model="apartment.data.apportionment"
        label="Coeficiente de prorrateo *"
        type="number"
        step="0.0001"
        min="0.0001"
        @keyup.enter="onSave"
      />
      <FormError
        :show="apartment.rules.apportionment"
        :message="apartment.messages.apportionment"
      />
      <BasicLabel label="Habitaciones" />
      <BorderInput
        v-model="apartment.data.bedrooms"
        label="Habitaciones"
        type="number"
        min="0"
        step="1"
        @keyup.enter="onSave"
      />
      <FormError
        :show="apartment.rules.bedrooms"
        :message="apartment.messages.bedrooms"
      />
      <BasicLabel label="Deuda actual" />
      <BorderInput
        v-model="apartment.data.totalDebt"
        type="number"
        label="Deuda actual"
        disabled
      />
      <p class="helper">Este valor se actualiza automaticamente al cerrar un periodo.</p>
    </div>
    <PrimaryButton label="Guardar" :click="onSave" />
  </div>
</template>

<script>
import BasicTitle from "../../widgets/title/BasicTitle";
import BasicLabel from "../../widgets/label/BasicLabel";
import BasicSubtitle from "../../widgets/subtitle/BasicSubtitle";
import BorderInput from "../../widgets/input/BorderInput";
import BorderSelect from "../../widgets/select/BorderSelect";
import FormError from "../../widgets/error/FormError";
import PrimaryButton from "../../widgets/button/PrimaryButton";
import store from "../../store";
import { onBeforeMount, onBeforeUnmount, reactive, watchEffect } from "vue";
import { useRouter, useRoute } from "vue-router";
import { openMode, actions } from "../../constants";

export default {
  components: {
    BasicTitle,
    BasicLabel,
    BasicSubtitle,
    BorderInput,
    BorderSelect,
    FormError,
    PrimaryButton,
  },
  setup() {
    const router = useRouter();
    const route = useRoute();

    const apartment = reactive({
      towers: [],
      data: {
        id: "",
        towerId: "",
        number: "",
        apportionment: "",
        bedrooms: "",
        totalDebt: "",
      },
      rules: {
        towerId: false,
        number: false,
        apportionment: false,
        bedrooms: false,
        totalDebt: false,
      },
      messages: {
        apportionment:
          "El coeficiente de prorrateo es obligatorio para apartamentos activos.",
        bedrooms: "Las habitaciones deben ser un numero entero mayor o igual a 0.",
      },
    });

    const normalizeNumberText = (value) =>
      String(value === undefined || value === null ? "" : value)
        .trim()
        .replace(",", ".");

    const hasMoreThanFourDecimals = (value) => {
      const decimals = normalizeNumberText(value).split(".")[1];
      return Boolean(decimals && decimals.length > 4);
    };

    const validateApportionment = () => {
      const normalized = normalizeNumberText(apartment.data.apportionment);
      const parsed = Number(normalized);

      if (!normalized) {
        apartment.messages.apportionment =
          "El coeficiente de prorrateo es obligatorio para apartamentos activos.";
        return false;
      }
      if (!Number.isFinite(parsed)) {
        apartment.messages.apportionment =
          "El coeficiente de prorrateo debe ser numerico.";
        return false;
      }
      if (parsed <= 0) {
        apartment.messages.apportionment =
          "El coeficiente de prorrateo debe ser mayor a 0.";
        return false;
      }
      if (hasMoreThanFourDecimals(normalized)) {
        apartment.messages.apportionment =
          "El coeficiente de prorrateo permite hasta 4 decimales.";
        return false;
      }

      apartment.data.apportionment = normalized;
      return true;
    };

    const validateBedrooms = () => {
      const value = normalizeNumberText(apartment.data.bedrooms);
      if (!value) return true;
      const parsed = Number(value);
      return Number.isInteger(parsed) && parsed >= 0;
    };

    const validate = () => {
      let valid = true;
      apartment.rules.towerId = false;
      apartment.rules.number = false;
      apartment.rules.apportionment = false;
      apartment.rules.bedrooms = false;

      if (!apartment.data.towerId) {
        apartment.rules.towerId = true;
        valid = false;
      }
      if (!apartment.data.number) {
        apartment.rules.number = true;
        valid = false;
      }
      if (!validateApportionment()) {
        apartment.rules.apportionment = true;
        valid = false;
      }
      if (!validateBedrooms()) {
        apartment.rules.bedrooms = true;
        valid = false;
      }

      return valid;
    };

    const onSave = async () => {
      if (!validate()) return;

      store.commit("setLoading", true);

      const request = {
        id: apartment.data.id,
        towerId: apartment.data.towerId,
        number: apartment.data.number,
        apportionment: apartment.data.apportionment,
        bedrooms: apartment.data.bedrooms,
        totalDebt: apartment.data.totalDebt,
      };

      let response = null;
      if (store.state.openMode === openMode.MODIFY) {
        response = await store.dispatch(
          actions.apartmentActions.update,
          request
        );
      } else {
        response = await store.dispatch(
          actions.apartmentActions.create,
          request
        );
      }
      store.commit("setLoading", false);
      if (response.ok) {
        store.commit("setSelectedApartments", []);
        router.push("/apartamentos/listado");
      } else {
        store.state.toast.add({
          severity: "error",
          summary: "",
          detail: response.message,
          life: 5000,
        });
      }
    };

    onBeforeMount(() => {
      if (!store.state.openMode) router.push("/apartamentos/listado");
      store.commit("addBreadcrumb", { label: route.name, to: route.path });
      const building = store.state.auth.user.buildings.find(
        (item) => item.id === store.state.general.selectedBuilding
      );
      if (building) {
        apartment.towers = building.towers;
      }
      if (store.state.openMode === openMode.MODIFY) {
        const selected = store.state.apartment.selectedApartments[0];
        apartment.data.id = selected.id;
        apartment.data.towerId = selected.tower.id;
        apartment.data.number = selected.number;
        apartment.data.apportionment = selected.apportionment;
        apartment.data.bedrooms = selected.bedrooms;
        apartment.data.totalDebt = selected.totalDebt;
      }
    });

    onBeforeUnmount(() => {
      store.commit("removeBreadcrumb");
    });

    watchEffect(() => {
      if (apartment.data.towerId) apartment.rules.towerId = false;
      if (apartment.data.number) apartment.rules.number = false;
      if (apartment.data.apportionment) apartment.rules.apportionment = false;
      if (validateBedrooms()) apartment.rules.bedrooms = false;
      if (apartment.data.totalDebt) apartment.rules.totalDebt = false;
    });

    return { store, apartment, onSave };
  },
};
</script>

<style scoped>
.container {
  display: flex;
  flex-direction: column;
  align-items: center;
}

.container-form {
  display: flex;
  flex-direction: column;
  align-items: center;
  margin: 2rem 0;
}
</style>
